import { useId } from 'react';

interface RatingStarProps {
  /** 1 to 5: which star this is. */
  index: number;
  /** The score being shown (a hover preview wins over the saved value). */
  shown: number | null;
  size: number;
  interactive: boolean;
  onHover: (value: number | null) => void;
  onPick?: (value: number) => void;
}

const STAR_PATH = 'M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3 6.1 20.6l1.3-6.6L2.5 9.4l6.6-.8z';

/** One star of the rating. When interactive, each half is its own click target. */
export function RatingStar({ index, shown, size, interactive, onHover, onPick }: RatingStarProps) {
  const gradientId = useId();
  const fill = shown === null ? 0 : Math.max(0, Math.min(1, shown - (index - 1)));
  const percent = `${fill * 100}%`;

  const half = (value: number, side: 'left' | 'right') => (
    <span
      data-half={value}
      aria-hidden="true"
      onMouseEnter={() => onHover(value)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onPick?.(value)}
      style={{
        position: 'absolute',
        [side]: 0,
        top: 0,
        width: '50%',
        height: '100%',
        cursor: 'pointer',
      }}
    />
  );

  return (
    <span style={{ position: 'relative', width: size, height: size, display: 'inline-block' }}>
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0 }}
      >
        <defs>
          <linearGradient id={gradientId}>
            <stop offset={percent} stopColor="var(--nb-warn)" />
            <stop offset={percent} stopColor="transparent" />
          </linearGradient>
        </defs>
        <path
          d={STAR_PATH}
          fill={`url(#${gradientId})`}
          stroke={shown === null ? 'var(--nb-border-2)' : 'var(--nb-warn)'}
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      </svg>
      {interactive && (
        <>
          {half(Math.max(1, index - 0.5), 'left')}
          {half(index, 'right')}
        </>
      )}
    </span>
  );
}
